import { Component } from '@theme/component';

/*
 * ---------------------------------------------------------------------------
 * Die Leinwand: macht das Video auf schwarzem Grund durchsichtig.
 * ---------------------------------------------------------------------------
 *
 * Die animierte Wortmarke liegt als MP4 auf Schwarz vor. Echte Transparenz
 * gaebe es nur als WebM mit Alphakanal - und genau den wirft iOS weg. Auch der
 * Umweg ueber mix-blend-mode: screen hilft dort nicht: Safari wendet
 * Mischungen auf Videos nicht an und laesst den schwarzen Kasten stehen.
 *
 * Also wird gerechnet statt gemischt. Jedes Einzelbild wandert auf eine
 * Leinwand, und ein winziges Programm auf der Grafikkarte bestimmt fuer jeden
 * Bildpunkt seine Deckkraft aus seiner Helligkeit: Schwarz wird vollstaendig
 * durchsichtig, Weiss bleibt vollstaendig stehen, Grauwerte dazwischen
 * entsprechend - das sind die weichen Kanten der Buchstaben.
 *
 * Dass das genau dem entspricht, was ein echter Alphakanal ergaebe, liegt an
 * der Vorlage: die Wortmarke ist reines Weiss auf reinem Schwarz. Ein Grauwert
 * g im Video ist dann nichts anderes als weisse Farbe mit der Deckkraft g,
 * bereits mit ihr verrechnet - und in genau dieser Form erwartet WebGL die
 * Farbe auch (premultipliedAlpha).
 */

const ECKPUNKTE = `
  attribute vec2 ort;
  uniform vec2 ausschnitt;
  varying vec2 stelle;
  void main() {
    // Nur die Mitte des Videobilds - dort steht die Wortmarke (siehe richteAus).
    stelle = ort * 0.5 * ausschnitt + 0.5;
    gl_Position = vec4(ort, 0.0, 1.0);
  }
`;

const PUNKTE = `
  precision mediump float;
  uniform sampler2D bild;
  varying vec2 stelle;
  void main() {
    vec3 farbe = texture2D(bild, stelle).rgb;
    // Die Deckkraft ist die Helligkeit - der hellste der drei Kanaele.
    float deckung = max(max(farbe.r, farbe.g), farbe.b);
    gl_FragColor = vec4(farbe, deckung);
  }
`;

/**
 * Uebersetzt ein Teilprogramm fuer die Grafikkarte.
 *
 * @param {WebGLRenderingContext} gl
 * @param {number} art
 * @param {string} quelle
 * @returns {WebGLShader | null}
 */
function uebersetze(gl, art, quelle) {
  const teil = gl.createShader(art);
  if (!teil) return null;

  gl.shaderSource(teil, quelle);
  gl.compileShader(teil);

  if (!gl.getShaderParameter(teil, gl.COMPILE_STATUS)) {
    gl.deleteShader(teil);
    return null;
  }

  return teil;
}

class LogoLeinwand {
  /**
   * @param {HTMLCanvasElement} leinwand
   * @param {HTMLVideoElement} video
   */
  constructor(leinwand, video) {
    this.leinwand = leinwand;
    this.video = video;
    this.laeuft = false;
  }

  /**
   * Richtet alles ein. Gibt zurueck, ob die Leinwand uebernehmen kann.
   *
   * @returns {boolean}
   */
  richteEin() {
    /*
     * Ohne preserveDrawingBuffer raeumt der Browser die Flaeche nach jedem
     * Zusammensetzen ab. Da die Animation am Ende stehenbleibt und dann nicht
     * mehr gezeichnet wird, waere die Wortmarke sonst weg, sobald sie fertig
     * ist.
     */
    const gl =
      this.leinwand.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: true,
      }) || null;

    if (!gl) return false;
    this.gl = gl;

    const ecken = uebersetze(gl, gl.VERTEX_SHADER, ECKPUNKTE);
    const punkte = uebersetze(gl, gl.FRAGMENT_SHADER, PUNKTE);
    if (!ecken || !punkte) return false;

    const programm = gl.createProgram();
    if (!programm) return false;

    gl.attachShader(programm, ecken);
    gl.attachShader(programm, punkte);
    gl.linkProgram(programm);
    if (!gl.getProgramParameter(programm, gl.LINK_STATUS)) return false;

    gl.useProgram(programm);

    // Zwei Dreiecke, die die ganze Flaeche ausfuellen.
    const feld = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, feld);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    this.ausschnittOrt = gl.getUniformLocation(programm, 'ausschnitt');
    gl.uniform2f(this.ausschnittOrt, 1, 1);

    const ort = gl.getAttribLocation(programm, 'ort');
    gl.enableVertexAttribArray(ort);
    gl.vertexAttribPointer(ort, 2, gl.FLOAT, false, 0, 0);

    this.textur = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.textur);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    // Videobilder kommen andersherum an, als WebGL sie erwartet.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

    return true;
  }

  /**
   * Passt die Aufloesung der Leinwand an ihre Groesse auf der Seite an.
   *
   * Feiner als das Video selbst braucht sie nicht zu sein, und feiner als der
   * Bildschirm ebenso wenig - beides waere nur Arbeit ohne sichtbaren Gewinn.
   */
  richteAus() {
    const { gl, leinwand, video } = this;
    if (!gl) return;

    /*
     * Die Leinwand ist so gross wie der Rahmen der Wortmarke, nicht wie das
     * hochskalierte Video. Frueher lag sie wie das Video mehr als doppelt so
     * breit und hoch um den Rahmen und wurde nur von overflow beschnitten.
     * Ihre unsichtbaren Raender reichten dabei bis unter die Knoepfe des
     * Heros - und weil sie jedes Einzelbild neu zeichnet, frischte Chrome
     * deren Milchglas genau bis zu ihrer Kante neu auf: In "Individuell
     * anfragen" blitzte waehrend der Animation eine senkrechte Linie auf.
     * Jetzt schneidet die Grafikkarte die Mitte des Videobilds selbst aus:
     * derselbe Ausschnitt wie vorher, ohne Ueberstand.
     *
     * Die Linie kam trotzdem wieder: Chrome zeichnet je Bild das Rechteck
     * um alles Bewegte neu, und das reicht von der Wortmarke bis zur Pille
     * unter den Knoepfen. Behoben ist sie am Glas selbst (divineprint.css,
     * dp-glas-takt) - fuer jede Ursache, nicht nur fuer diese Leinwand.
     */
    const rahmen = leinwand.parentElement;
    const rb = rahmen ? rahmen.clientWidth : leinwand.clientWidth;
    const rh = rahmen ? rahmen.clientHeight : leinwand.clientHeight;
    const vb = video.videoWidth || 1920;
    const vh = video.videoHeight || 1080;
    const skala = (rahmen && parseFloat(getComputedStyle(rahmen).getPropertyValue('--dp-logo-video-scale'))) / 100 || 2.217;
    const ax = Math.min(1, 1 / skala);
    const ay = rb ? Math.min(1, rh / ((rb * skala * vh) / vb)) : 1;

    const dichte = Math.min(window.devicePixelRatio || 1, 2);
    // Feiner als der Ausschnitt im Video lohnt nicht.
    const breite = Math.max(1, Math.min(Math.round(rb * dichte) || 1, Math.round(vb * ax)));
    const hoehe = Math.max(1, Math.round((breite * rh) / (rb || 1)) || 1);

    if (this.ausschnittOrt) gl.uniform2f(this.ausschnittOrt, ax, ay);

    if (leinwand.width === breite && leinwand.height === hoehe) return;

    leinwand.width = breite;
    leinwand.height = hoehe;
    gl.viewport(0, 0, breite, hoehe);
  }

  /** Holt das aktuelle Bild aus dem Video und zeichnet es. */
  zeichne() {
    const { gl, video } = this;
    if (!gl || video.readyState < 2) return;

    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
    } catch {
      // Ein einzelnes Bild kann fehlschlagen, etwa waehrend eines Sprungs.
      return;
    }

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  /**
   * Zeichnet Bild fuer Bild, solange das Video laeuft.
   *
   * requestVideoFrameCallback meldet sich genau dann, wenn ein neues Bild
   * bereitsteht - kein Bild wird doppelt gezeichnet und keines uebersprungen.
   * Wo es das nicht gibt, tut es der gewoehnliche Bildtakt auch.
   */
  starte() {
    if (this.laeuft) return;
    this.laeuft = true;

    const { video } = this;
    const proBild = typeof video.requestVideoFrameCallback === 'function';

    const schritt = () => {
      if (!this.laeuft) return;

      this.zeichne();

      /*
       * Steht das Video, ist die Animation an ihrem Halt angekommen. Das
       * letzte Bild ist gerade gezeichnet worden und bleibt stehen.
       */
      if (video.paused || video.ended) {
        this.laeuft = false;
        return;
      }

      if (proBild) video.requestVideoFrameCallback(schritt);
      else this.naechstes = requestAnimationFrame(schritt);
    };

    if (proBild) video.requestVideoFrameCallback(schritt);
    else this.naechstes = requestAnimationFrame(schritt);
  }

  /**
   * Nach einer Groessenaenderung muss die Flaeche neu aufgezogen werden - das
   * loescht sie, also wird gleich noch einmal gezeichnet.
   */
  beiGroesse() {
    const vorher = this.leinwand.width;
    this.richteAus();
    if (this.leinwand.width !== vorher) this.zeichne();
  }

  halt() {
    this.laeuft = false;
    if (this.naechstes) cancelAnimationFrame(this.naechstes);
  }
}

/**
 * Spielt die animierte DivinePrint-Wortmarke ab und haelt am fertigen Logo an.
 *
 * Der Block liefert drei Fassungen aus: das Video auf schwarzem Grund, die
 * Leinwand, die es durchsichtig macht (siehe oben), und die statische
 * Inline-SVG-Wortmarke als Rueckfall.
 *
 * Sichtbar ist standardmaessig nur das Bewegtbild. Die statische Marke tritt
 * erst ein, wenn das Video nicht laufen kann - bei reduzierter Bewegung oder
 * wenn der Browser das Autoplay verweigert. So blitzt vor dem Start nichts auf.
 *
 * Die Animation blendet gegen Ende bewusst wieder aus und endet auf einem
 * leeren Bild. Mit `data-hold-at` groesser 0 laesst sie sich vorher anhalten;
 * bei 0 laeuft sie vollstaendig durch und die Flaeche bleibt danach leer.
 *
 * Es wird bewusst nicht ueberblendet: Video und SVG liegen deckungsgleich
 * uebereinander, ein Crossfade wuerde nur doppelte Kanten erzeugen.
 *
 * Zweite Aufgabe des Blocks ist die Uebergabe an den Header: solange die
 * Wortmarke hier im Hero steht, bleibt sie im Header ausgeblendet. Erst wenn
 * sie nach oben aus dem Bild gescrollt ist, taucht sie oben wieder auf - die
 * Marke ist damit immer genau einmal zu sehen. Siehe Abschnitt 8 in
 * divineprint.css.
 *
 * @typedef {object} DpLogoAnimationRefs
 * @property {HTMLVideoElement} video - Das Video mit der animierten Wortmarke.
 * @property {HTMLCanvasElement} [leinwand] - Flaeche, die es durchsichtig macht.
 *
 * @extends {Component<DpLogoAnimationRefs>}
 */
export class DpLogoAnimation extends Component {
  requiredRefs = ['video'];

  static FALLBACK_CLASS = 'dp-logo-animation--fallback';

  /** Gesetzt, sobald die Leinwand das Bild vom Video uebernommen hat. */
  static LEINWAND_CLASS = 'dp-logo-animation--leinwand';

  /** Kommt die Leinwand nicht zustande, zeigt sich das Video selbst. */
  static VIDEO_CLASS = 'dp-logo-animation--video';

  /** Zusaetzlicher Weg, bevor die Marke oben erscheint - verhindert Flackern
   *  genau an der Kante und laesst die Uebergabe ruhiger wirken. */
  static PUFFER = 40;

  /** So lange wird hoechstens auf den vollstaendigen Film gewartet. */
  static LADEFRIST = 8000;

  /**
   * Ruhepause zwischen dem fertig geladenen Film und dem ersten Bild.
   *
   * Der Film liegt dann zwar vollstaendig vor, aber die Seite ist noch bei der
   * Arbeit: Bilder werden dekodiert, Schriften gesetzt, Skripte laufen an. Die
   * ersten Bilder der Animation fielen genau in diese Zeit und kamen sichtbar
   * stockend. Ein halber Atemzug spaeter hat die Seite Ruhe.
   */
  static ANLAUF = 300;

  /** So lange wird hoechstens auf das Ende des Seitenaufbaus gewartet. */
  static WARTEDECKEL = 1200;

  connectedCallback() {
    super.connectedCallback();

    // Die Uebergabe an den Header steht vor dem Video: sie gilt auch dann,
    // wenn statt der Animation die statische Wortmarke gezeigt wird.
    this.beobachteUebergabe();

    const { video } = this.refs;
    if (!(video instanceof HTMLVideoElement)) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Keine Bewegung gewuenscht: die statische Wortmarke tritt an die Stelle
      // der Animation.
      this.classList.add(DpLogoAnimation.FALLBACK_CLASS);
      return;
    }

    this.holdAt = Number.parseFloat(this.dataset.holdAt ?? '0') || 0;

    this.handleTimeUpdate = () => {
      if (this.holdAt > 0 && video.currentTime >= this.holdAt) {
        video.pause();
        video.removeEventListener('timeupdate', this.handleTimeUpdate);
      }
    };

    video.addEventListener('timeupdate', this.handleTimeUpdate);

    this.richteLeinwandEin();
    this.ladeUndStarte(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    // Der Film liegt im Speicher, solange diese Adresse gilt.
    if (this.blobAdresse) URL.revokeObjectURL(this.blobAdresse);
  }

  /**
   * Laedt den Film vollstaendig und startet ihn erst danach.
   *
   * Bis dahin lief er, sobald die ersten Bilder da waren, und holte den Rest
   * waehrend des Abspielens nach. Beim ersten Besuch - wenn nichts im
   * Zwischenspeicher liegt - stockte die Animation dadurch sichtbar: sie ist
   * nur vier Sekunden lang, jede Nachladepause faellt darin auf. Ausserdem
   * haengt an ihr die Uebergabe der Wortmarke an die Kopfzeile, die auf die
   * Laufzeit hoert.
   *
   * Geholt wird der Film deshalb im Ganzen und dem Videoelement als Adresse im
   * Speicher gereicht. Danach kann nichts mehr nachzuladen sein.
   *
   * Schlaegt das fehl - eine Sperre der Herkunft etwa -, bleibt der gewohnte
   * Weg: das Element laedt selbst, und gewartet wird auf seine Meldung, dass es
   * durchlaufen kann.
   *
   * @param {HTMLVideoElement} video
   */
  async ladeUndStarte(video) {
    const quelle = video.querySelector('source[type="video/mp4"]');
    const ausweich = video.querySelector('source[type="video/webm"]');
    const adresse =
      quelle && video.canPlayType('video/mp4') ? quelle.src : ausweich?.src || quelle?.src;

    if (adresse) {
      try {
        const antwort = await fetch(adresse, { credentials: 'omit' });
        if (antwort.ok) {
          this.blobAdresse = URL.createObjectURL(await antwort.blob());
          video.src = this.blobAdresse;
        }
      } catch {
        /* Dann eben auf dem gewohnten Weg. */
      }
    }

    // Ohne src laedt das Element seine eigenen Quellen - preload steht auf none.
    if (!this.blobAdresse) video.preload = 'auto';
    video.load();

    await this.wartetAufDenGanzenFilm(video);
    await this.wartetAufRuhe();

    // Zwischenzeitlich vom Betrachter weggeraeumt.
    if (!this.isConnected) return;

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        // Autoplay verweigert, etwa im Energiesparmodus: statische Fassung zeigen.
        this.classList.add(DpLogoAnimation.FALLBACK_CLASS);
        this.leinwand?.halt();
      });
    }
  }

  /**
   * Wartet, bis die Seite fertig geladen ist und kurz durchgeatmet hat.
   *
   * Das Laden selbst kostet Rechenzeit, und die fehlt genau dort, wo die
   * Animation ihre ersten Bilder braucht. Erst laeuft also die Seite zu Ende,
   * dann vergeht die Ruhepause, dann faengt der Film an.
   *
   * Gewartet wird dabei hoechstens WARTEDECKEL lang. Eine Seite, die lange
   * laedt - ein grosses Bild, eine langsame Verbindung -, darf die Wortmarke
   * nicht beliebig lange zurueckhalten: bis sie laeuft, steht an ihrer Stelle
   * nichts, und wer nichts sieht, haelt sie fuer kaputt.
   *
   * @returns {Promise<void>}
   */
  async wartetAufRuhe() {
    if (document.readyState !== 'complete') {
      await Promise.race([
        new Promise((fertig) => window.addEventListener('load', fertig, { once: true })),
        new Promise((fertig) => setTimeout(fertig, DpLogoAnimation.WARTEDECKEL)),
      ]);
    }

    await new Promise((fertig) => setTimeout(fertig, DpLogoAnimation.ANLAUF));

    // Ein Bild abwarten: der naechste Zeichenschritt gehoert dann der Animation.
    await new Promise((fertig) => requestAnimationFrame(() => fertig()));
  }

  /**
   * Wartet, bis der Browser den Film ohne Pause durchspielen kann.
   *
   * Aus dem Speicher heraus ist das sofort der Fall; ueber die Leitung meldet
   * es der Browser mit "canplaythrough". Die Frist ist der Notausgang: meldet
   * sich nichts - manche Browser sparen das Ereignis in Hintergrundtabs ein -,
   * laeuft die Animation trotzdem an, dann eben wie bisher.
   *
   * @param {HTMLVideoElement} video
   * @returns {Promise<void>}
   */
  wartetAufDenGanzenFilm(video) {
    if (video.readyState >= 4) return Promise.resolve();

    return new Promise((fertig) => {
      const schluss = () => {
        video.removeEventListener('canplaythrough', schluss);
        clearTimeout(frist);
        fertig();
      };

      const frist = setTimeout(schluss, DpLogoAnimation.LADEFRIST);
      video.addEventListener('canplaythrough', schluss, { once: true });
    });
  }

  /**
   * Uebergibt das Bild an die Leinwand, sobald die Masse des Videos feststehen.
   *
   * Sie uebernimmt sofort, nicht erst nach dem ersten gezeichneten Bild: bis
   * dahin ist sie leer und damit durchsichtig, waehrend das Video an ihrer
   * Stelle auf iOS als schwarzer Kasten stuende. Nichts zu sehen ist an dieser
   * Stelle also besser als das Falsche.
   *
   * Bis dahin ist ueberhaupt nichts zu sehen: das Video beginnt verborgen,
   * sonst blitzt sein schwarzer Kasten beim Laden kurz auf.
   *
   * Kommt keine Leinwand zustande, tritt das Video an ihre Stelle - mit der
   * Mischung, die ueberall ausser auf iOS greift.
   */
  richteLeinwandEin() {
    const flaeche = this.refs.leinwand;
    const { video } = this.refs;
    if (!(flaeche instanceof HTMLCanvasElement) || !(video instanceof HTMLVideoElement)) {
      this.classList.add(DpLogoAnimation.VIDEO_CLASS);
      return;
    }

    const los = () => {
      const leinwand = new LogoLeinwand(flaeche, video);
      if (!leinwand.richteEin()) {
        this.classList.add(DpLogoAnimation.VIDEO_CLASS);
        return;
      }

      this.leinwand = leinwand;
      leinwand.richteAus();
      leinwand.zeichne();
      this.classList.add(DpLogoAnimation.LEINWAND_CLASS);

      /*
       * Gezeichnet wird erst, wenn das Video wirklich laeuft.
       *
       * Die Schleife hoert von selbst auf, sobald das Video steht - so bleibt
       * am Halt das letzte Bild stehen, ohne dass weiter gerechnet wird. Genau
       * deshalb darf sie nicht vorher anfangen: seit der Film erst nach einer
       * Ruhepause startet, faende sie ein stehendes Video vor, hoerte sofort
       * wieder auf, und die Flaeche bliebe leer - zu sehen waere nichts.
       *
       * "playing" und nicht "play": es meldet den Moment, in dem tatsaechlich
       * Bilder kommen. Ohne once, damit die Schleife auch nach einer Pause
       * wieder anlaeuft.
       */
      video.addEventListener('playing', () => leinwand.starte());

      this.beiGroesseDerLeinwand = () => leinwand.beiGroesse();
      window.addEventListener('resize', this.beiGroesseDerLeinwand, { passive: true });
    };

    if (video.readyState >= 1) los();
    else video.addEventListener('loadedmetadata', los, { once: true });
  }

  /**
   * Meldet dem Dokument, ob die Wortmarke im Header stehen darf.
   *
   * Gemessen wird bei jeder Bewegung frisch: liegt die Unterkante der
   * Wortmarke weit genug ueber der Unterkante des Headers, uebernimmt der
   * Header sie. Der Puffer sorgt dafuer, dass sie erst ein Stueck spaeter
   * erscheint - direkt an der Kante wuerde es flackern.
   *
   * Der Kern steht in einem einzigen Wort: capture. Horizon laesst ab 990px
   * nicht das Dokument scrollen, sondern .page-wrapper, und darunter das
   * Dokument selbst. Scroll-Ereignisse steigen nicht auf, ein gewoehnlicher
   * Listener am window bekommt vom .page-wrapper also nie etwas mit - genau
   * daran ist die Uebergabe auf dem Desktop vorher gescheitert. In der
   * Einfangphase laeuft jedes Ereignis dagegen von oben nach unten durch, auch
   * ein nicht aufsteigendes. Ein Listener faengt damit beide Faelle ab, ohne
   * zu wissen, welches Element gerade scrollt, und ohne beim Wechsel ueber die
   * 990px-Grenze neu angehaengt werden zu muessen.
   *
   * getBoundingClientRect misst immer gegen das sichtbare Fenster, unabhaengig
   * davon, welches Element scrollt. Die Rechnung bleibt also in beiden Faellen
   * dieselbe.
   */
  beobachteUebergabe() {
    const header = document.querySelector('#header-component');

    const pruefe = () => {
      this.naechstesBild = null;
      /*
       * Zeigt der Hero gerade eine Folie (dp-hero-folien.js), ist die
       * Wortmarke dorthin in den Header gewichen - dann steht sie dort, ganz
       * gleich, wie weit gescrollt ist.
       */
      if (document.documentElement.dataset.dpHeroFolie !== undefined) {
        this.melde(true);
        return;
      }
      const kante = header ? header.getBoundingClientRect().bottom : 0;
      this.melde(this.getBoundingClientRect().bottom <= kante - DpLogoAnimation.PUFFER);
    };
    /* Von aussen anzustossen - der Hero meldet so einen Wechsel der Folie. */
    this.pruefeUebergabe = pruefe;

    // Hoechstens eine Messung je Bild - Scrollen feuert deutlich oefter.
    this.beiBewegung = () => {
      if (this.naechstesBild) return;
      this.naechstesBild = requestAnimationFrame(pruefe);
    };

    this.lauscher = { passive: true, capture: true };
    window.addEventListener('scroll', this.beiBewegung, this.lauscher);
    window.addEventListener('resize', this.beiBewegung, { passive: true });

    pruefe();
  }

  /** @param {boolean} sichtbar Ob der Header seine Wortmarke zeigen soll. */
  melde(sichtbar) {
    const wert = sichtbar ? 'an' : 'aus';
    // Bei jedem Bild dasselbe Attribut neu zu setzen waere unnoetige Arbeit
    // fuer den Browser.
    if (document.documentElement.dataset.dpHeaderLogo === wert) {
      /*
       * Auch ein Zustand, der schon steht, ist eine Meldung.
       *
       * Auf der Startseite liefert Liquid ihn im Markup mit (layout/theme.liquid),
       * damit das Menue vom ersten Bild an richtig sitzt. Die erste Pruefung
       * findet ihn deshalb unveraendert vor - ohne diese Zeile bliebe sie
       * ungezaehlt, und der erste echte Wechsel beim Scrollen galte als der
       * erste ueberhaupt und wuerde ohne Bewegung ausgefuehrt.
       */
      this.schonGemeldet = true;
      return;
    }

    const menue = document.querySelector('.header__column--center');
    const vorher = menue ? menue.getBoundingClientRect().left : null;

    document.documentElement.dataset.dpHeaderLogo = wert;

    // Beim ersten Mal steht der Zustand nur fest - da gibt es nichts zu bewegen.
    if (this.schonGemeldet) this.gleiteMenue(menue, vorher);
    this.schonGemeldet = true;
  }

  /**
   * Laesst das Menue von seiner alten an seine neue Stelle gleiten.
   *
   * Die Spalten des Headers wechseln zwischen Inhaltsbreite und gleichmaessiger
   * Aufteilung. Solche Werte lassen sich nicht ueberblenden - ein Wechsel waere
   * ein harter Sprung. Also wird gemessen, wo das Menue vorher stand, es per
   * Verschiebung dorthin zurueckgesetzt und von da an seine neue Stelle
   * bewegt. Das Layout ist die ganze Zeit ueber schon das neue, nur das Bild
   * holt auf.
   *
   * @param {Element | null} menue
   * @param {number | null} vorher Linke Kante vor dem Wechsel.
   */
  gleiteMenue(menue, vorher) {
    if (!menue || vorher == null) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const versatz = vorher - menue.getBoundingClientRect().left;
    if (Math.abs(versatz) < 1) return;

    menue.style.transition = 'none';
    menue.style.transform = 'translateX(' + versatz + 'px)';

    requestAnimationFrame(() => {
      menue.style.transition = 'transform var(--dp-duration) var(--dp-ease)';
      menue.style.transform = '';

      /*
       * Am Ende werden beide Angaben entfernt. Ein stehengebliebenes transform
       * wuerde einen eigenen Bezugsrahmen aufspannen, an dem sich das
       * aufklappende Menue ausrichtet - es saesse dann falsch.
       */
      const aufraeumen = () => {
        menue.style.transition = '';
        menue.style.transform = '';
        menue.removeEventListener('transitionend', aufraeumen);
      };
      menue.addEventListener('transitionend', aufraeumen);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { video } = this.refs;
    if (video instanceof HTMLVideoElement && this.handleTimeUpdate) {
      video.removeEventListener('timeupdate', this.handleTimeUpdate);
    }

    this.leinwand?.halt();
    if (this.beiGroesseDerLeinwand) window.removeEventListener('resize', this.beiGroesseDerLeinwand);

    if (this.naechstesBild) cancelAnimationFrame(this.naechstesBild);
    if (this.beiBewegung) {
      window.removeEventListener('scroll', this.beiBewegung, this.lauscher);
      window.removeEventListener('resize', this.beiBewegung);
    }

    // Ohne Hero im Dokument gibt es nichts mehr zu verstecken - etwa wenn der
    // Block im Theme-Editor entfernt wird.
    this.melde(true);
  }
}

if (!customElements.get('dp-logo-animation')) {
  customElements.define('dp-logo-animation', DpLogoAnimation);
}
